import type { GoodsDirection, GoodsVoucherPayload } from '../../lib/types';

export interface LocalGoodsVoucherDraft {
  companyId: string;
  direction: GoodsDirection;
  payload: GoodsVoucherPayload;
  idempotencyKey: string;
  savedAt: string;
}
const DATABASE = 'accounting-platform-goods-voucher-drafts';
const STORE = 'new-goods-voucher-by-company-direction';
const key = (companyId: string, direction: GoodsDirection) => `${companyId}:${direction}`;
function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) return reject(new Error('This browser does not provide local draft storage.'));
    const request = window.indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: 'key' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open local draft storage.'));
  });
}
async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      let value: T;
      const request = fn(transaction.objectStore(STORE));
      request.onsuccess = () => {
        value = request.result;
      };
      request.onerror = () => reject(request.error ?? new Error('Draft storage failed.'));
      transaction.oncomplete = () => resolve(value!);
      transaction.onerror = () => reject(transaction.error ?? new Error('Draft storage failed.'));
    });
  } finally {
    db.close();
  }
}
export async function loadGoodsVoucherDraft(
  companyId: string,
  direction: GoodsDirection
): Promise<LocalGoodsVoucherDraft | null> {
  const result = await tx<any>('readonly', (s) => s.get(key(companyId, direction)));
  return result?.companyId === companyId && result?.direction === direction ? result : null;
}
export async function saveGoodsVoucherDraft(draft: LocalGoodsVoucherDraft): Promise<void> {
  await tx('readwrite', (s) => s.put({ ...draft, key: key(draft.companyId, draft.direction) }));
}
export async function clearGoodsVoucherDraft(companyId: string, direction: GoodsDirection): Promise<void> {
  await tx('readwrite', (s) => s.delete(key(companyId, direction)));
}
